import { useEffect, useMemo, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { MdContentCopy } from 'react-icons/md';
import { successMessage } from '@/utils/toast';
import { decodeName } from '../utils/decodeName';
import { buildTreeModel } from '../utils/treeModel';
import { computeTreeStats } from '../utils/treeStats';
import ClusterExportToolbar from './ClusterExportToolbar';
import type {
  ClusterData,
  ClusterModeId,
  ClusterTableRow,
  HierarchicalNode,
  VolumeThresholds
} from '../types';

type ClusterCardViewProps = {
  hierarchical: HierarchicalNode | null;
  rows?: ClusterTableRow[];
  clusterData?: ClusterData | null;
  clusterMode?: ClusterModeId | string;
  onClusterModeChange?: (mode: ClusterModeId | string) => void;
  clusterSummary?: string | null;
  thresholds?: VolumeThresholds | null;
};

type CardItem = {
  key: string;
  node: HierarchicalNode;
};

/** Collect all nodes at an absolute tree depth (root = 0). */
function collectAtDepth(
  node: HierarchicalNode | null | undefined,
  targetDepth: number,
  currentDepth = 0,
  path: string[] = []
): CardItem[] {
  if (!node) return [];
  const nextPath = [...path, node.name];
  if (currentDepth === targetDepth) {
    return [{ key: nextPath.join('/'), node }];
  }
  if (currentDepth > targetDepth) return [];
  return (node.children ?? []).flatMap(child =>
    collectAtDepth(child, targetDepth, currentDepth + 1, nextPath)
  );
}

function defaultDepthForTree(maxDepth: number): number {
  if (maxDepth < 1) return 1;
  return Math.min(2, maxDepth);
}

export default function ClusterCardView({
  hierarchical,
  rows = [],
  clusterData,
  clusterMode = 'default',
  onClusterModeChange,
  clusterSummary = '',
  thresholds = null
}: ClusterCardViewProps) {
  const maxDepth = useMemo(() => {
    const model = buildTreeModel(hierarchical);
    return computeTreeStats(model.nodesById).maxDepth;
  }, [hierarchical]);

  const [depthFilter, setDepthFilter] = useState(() =>
    defaultDepthForTree(maxDepth)
  );

  // Reset when tree/mode changes; default 2, or the deepest available if shallower.
  useEffect(() => {
    setDepthFilter(defaultDepthForTree(maxDepth));
  }, [clusterMode, hierarchical, maxDepth]);

  // Cards at depth (N - 1), body lists children at depth N — matches current depth-2 UI.
  const cardDepth = Math.max(0, depthFilter - 1);
  const cards = useMemo(
    () => collectAtDepth(hierarchical, cardDepth),
    [hierarchical, cardDepth]
  );

  return (
    <div className="d-flex flex-column gap-3" dir="rtl">
      <ClusterExportToolbar
        rows={rows}
        clusterData={clusterData}
        clusterMode={clusterMode}
        onClusterModeChange={onClusterModeChange}
        pathCount={rows.length}
        clusterSummary={clusterSummary}
        thresholds={thresholds}
        depthFilter={depthFilter}
        onDepthFilterChange={setDepthFilter}
        maxDepth={maxDepth}
      />

      <Row className="g-3">
        {cards.map(({ key, node: item }) => {
          const childNames = (item.children || []).map(child =>
            decodeName(child.name)
          );
          const showChildren = depthFilter > cardDepth;

          return (
            <Col xs={12} md={6} xxl={3} key={key}>
              <Card className="h-100">
                <FalconCardHeader
                  title={<div>{decodeName(item.name)}</div>}
                  titleTag="h6"
                  className="py-2 fs-0 fw-bolder"
                  light
                  endEl={
                    <MdContentCopy
                      title="Copy"
                      onClick={() => {
                        const copyText = showChildren
                          ? decodeName(item.name) +
                            '\n\n' +
                            [...new Set(childNames)].join('\n')
                          : decodeName(item.name);
                        navigator.clipboard
                          .writeText(copyText)
                          .then(() => successMessage('با موفقیت کپی شد.'));
                      }}
                      className="cursor-pointer little-icon"
                      size={18}
                    />
                  }
                />
                <Card.Body
                  className="fs--1"
                  style={{ height: 300, overflow: 'auto' }}
                >
                  {!showChildren ||
                  !item.children ||
                  item.children.length === 0 ? (
                    <div>داده‌ای موجود نیست</div>
                  ) : (
                    item.children.map(child => (
                      <div key={`${key}/${child.name}`}>
                        {decodeName(child.name)}
                      </div>
                    ))
                  )}
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
}
